import React from "react";
import { Check, ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { CentralizedDrawer, useGlobalDrawerStack } from "@west-tennessee-consulting/ui";
import { SelectOption } from "./types";
import { Command, CommandList, CommandItem } from "@/components/ui/command";

interface MobileSelectDrawerProps {
  value: string;
  onValueChange: (value: string, option?: SelectOption) => void;
  options: SelectOption[];
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  error?: boolean;
  title?: string;
  description?: string;
  searchable?: boolean;
  searchPlaceholder?: string;
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  isSearching?: boolean;
  customOptionRenderer?: (option: SelectOption) => React.ReactNode;
  side?: "top" | "bottom" | "left" | "right";
  fullWidth?: boolean;
}

export const MobileSelectDrawer: React.FC<MobileSelectDrawerProps> = ({
  value,
  onValueChange,
  options,
  placeholder = "Select an option...",
  disabled = false,
  className = "",
  error = false,
  title,
  description,
  searchable = false,
  searchPlaceholder = "Search options...",
  searchValue,
  onSearchChange,
  isSearching = false,
  customOptionRenderer,
  side = "bottom",
  fullWidth = true,
}) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const [internalSearchTerm, setInternalSearchTerm] = React.useState("");
  const { openDrawer, closeDrawer } = useGlobalDrawerStack();
  const hasOpenedRef = React.useRef(false);

  // Generate unique drawer ID for this instance to avoid conflicts with multiple selects
  const drawerId = React.useMemo(() => {
    return `mobile-select-drawer-${Math.random().toString(36).substr(2, 9)}`;
  }, []);
  const effectiveSearchTerm = searchValue !== undefined ? searchValue : internalSearchTerm;

  // Filter options based on search term
  const filteredOptions = React.useMemo(() => {
    if (!searchable || !effectiveSearchTerm) return options;
    return options.filter(
      (option) =>
        option.label.toLowerCase().includes(effectiveSearchTerm.toLowerCase()) ||
        option.value.toLowerCase().includes(effectiveSearchTerm.toLowerCase()) ||
        (option.description &&
          option.description.toLowerCase().includes(effectiveSearchTerm.toLowerCase()))
    );
  }, [options, effectiveSearchTerm, searchable]);

  // Find selected option
  const selectedOption = React.useMemo(() => {
    return options.find((option) => option.value === value);
  }, [options, value]);

  // Reset search when drawer closes
  React.useEffect(() => {
    if (isOpen) {
      hasOpenedRef.current = true;
      return;
    }

    if (!hasOpenedRef.current) return;

    setInternalSearchTerm("");
    if (onSearchChange) onSearchChange("");
    closeDrawer(drawerId);
    hasOpenedRef.current = false;
  }, [isOpen, onSearchChange, drawerId, closeDrawer]);

  const handleValueChange = React.useCallback(
    (newValue: string) => {
      const option = options.find((opt) => opt.value === newValue);
      onValueChange(newValue, option);
      setIsOpen(false);
      closeDrawer(drawerId);
    },
    [options, onValueChange, drawerId, closeDrawer]
  );

  const handleSearchChange = React.useCallback(
    (val: string) => {
      if (onSearchChange) {
        onSearchChange(val);
      } else {
        setInternalSearchTerm(val);
      }
    },
    [onSearchChange]
  );

  const renderOption = React.useCallback(
    (option: SelectOption) => {
      if (customOptionRenderer) {
        return customOptionRenderer(option);
      }
      return option.label;
    },
    [customOptionRenderer]
  );

  return (
    <>
      <Button
        type="button"
        variant="outline"
        role="combobox"
        aria-expanded={isOpen}
        className={cn(
          "justify-between",
          fullWidth ? "w-full" : "w-auto min-w-[200px]",
          error && "border-red-500",
          disabled && "opacity-50 cursor-not-allowed",
          className
        )}
        onClick={() => {
          if (!disabled) {
            openDrawer(drawerId);
            setIsOpen(true);
          }
        }}
        disabled={disabled}
      >
        <span className="truncate">{selectedOption ? selectedOption.label : placeholder}</span>
        <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
      </Button>

      <CentralizedDrawer
        drawerId={drawerId}
        side={side}
        title={title || "Select Option"}
        description={description || "Choose an option from the list"}
        showCloseButton={true}
        enableSwipeGesture={true}
        zIndex={10000}
      >
        <div className="flex-1 min-h-0 overflow-y-auto py-2">
          <Command className="h-full">
            <CommandList className="max-h-none" style={{ maxHeight: "none" }}>
              {filteredOptions.map((option) => (
                <CommandItem
                  key={option.value}
                  onSelect={() => !option.disabled && handleValueChange(option.value)}
                  disabled={option.disabled}
                  className={cn(
                    "w-full justify-start min-h-[56px] px-6 py-4 rounded-none border-b border-gray-100 last:border-b-0 transition-colors duration-150",
                    option.value === value && "bg-primary/10 text-primary border-l-2 border-l-primary",
                    option.disabled && "opacity-50 cursor-not-allowed",
                    "hover:bg-muted active:bg-muted/80"
                  )}
                >
                  <div className="flex items-center w-full">
                    <div className="flex-1 text-left text-base">{renderOption(option)}</div>
                    {option.value === value && (
                      <Check className="ml-2 h-4 w-4 shrink-0 text-primary" />
                    )}
                  </div>
                </CommandItem>
              ))}
            </CommandList>
          </Command>
        </div>
      </CentralizedDrawer>
    </>
  );
};
